import { useCallback, useMemo } from 'react';

import { useLocalStorage } from 'ui/Hooks';

type InternalToolStorage = {
  value: boolean;
  label: string;
  tooltip?: string;
};
interface InternalTool extends InternalToolStorage {
  key: string;
  onChange: (value: boolean) => void;
  checked: boolean;
}

type InternalToolsMenu = {
  'feature-flag-override': InternalToolStorage;
};

const DEFAULT_INTERNAL_TOOLS_MENU: InternalToolsMenu = {
  'feature-flag-override': {
    value: false,
    label: 'Feature Flag Override',
    tooltip: 'Override feature flags for testing purposes.',
  },
};

const useInternalToolsMenu = () => {
  const [internalToolsMenu, setInternalToolsMenu] = useLocalStorage<InternalToolsMenu>({
    key: 'givebacks-internal-tools-menu',
    defaultValue: DEFAULT_INTERNAL_TOOLS_MENU,
  });

  const setToolValue = useCallback(
    (key: keyof InternalToolsMenu, value: boolean) => {
      setInternalToolsMenu((prev) => {
        prev ??= DEFAULT_INTERNAL_TOOLS_MENU;
        return { ...prev, [key]: { ...prev[key], value } };
      });
    },
    [setInternalToolsMenu]
  );

  const internalToolList = useMemo(() => {
    const tools = Object.entries(internalToolsMenu ?? {}) as [
      keyof InternalToolsMenu,
      InternalToolStorage,
    ][];
    if (tools.length === 0) {
      return undefined;
    }
    const toolsWithOnChange = tools.map(([key, toolValue]) => ({
      key,
      ...toolValue,
      onChange: (value: boolean) => setToolValue(key, value),
      checked: toolValue.value,
    }));
    return toolsWithOnChange;
  }, [internalToolsMenu, setToolValue]);

  const getToolValue = useCallback(
    (key: keyof InternalToolsMenu) => internalToolsMenu?.[key]?.value,
    [internalToolsMenu]
  );

  /** Sync Local storage with the Default Overrides. This aligns labels/tooltips etc without altering values
   * This should only be called once from the tools Menu
   */
  const syncLocalStorage = useCallback(() => {
    setInternalToolsMenu((prev) => {
      if (!prev) return DEFAULT_INTERNAL_TOOLS_MENU;
      if (prev === DEFAULT_INTERNAL_TOOLS_MENU) return prev;
      const keys = Object.entries(DEFAULT_INTERNAL_TOOLS_MENU);

      const updated = keys.reduce((acc, [key, tool]) => {
        const _tool = { ...tool, value: prev[key]?.value ?? tool.value };
        return { ...acc, [key]: _tool };
      }, {} as InternalToolsMenu);
      return updated;
    });
  }, [setInternalToolsMenu]);
  const resetMenu = useCallback(() => {
    setInternalToolsMenu(DEFAULT_INTERNAL_TOOLS_MENU);
  }, [setInternalToolsMenu]);

  const isToolsLoading = useMemo(() => {
    if (import.meta.env.VITE_API_ENV === 'production') {
      return false;
    } else {
      return Object.keys(internalToolList ?? {}).length == 0;
    }
  }, [internalToolList]);

  return {
    internalToolList,
    getToolValue,
    syncLocalStorage,
    resetMenu,
    isToolsLoading,
  };
};
export type { InternalTool };

export { useInternalToolsMenu };
